import React, { useState } from 'react';
import styles from './ProductRegistration.module.css';

const ProductRegistration = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    product: '',
    productId: '',
    agreeToTerms: false,
    marketingConsent: false,
    personalData: false,
    recaptcha: ''
  });

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // Sample products data
  const products = [
    { slug: 'drone-x1', title: 'Drone X1 Pro' },
    { slug: 'drone-x2', title: 'Drone X2 Elite' },
    { slug: 'drone-x3', title: 'Drone X3 Advanced' },
    { slug: 'camera-gimbal', title: 'Camera Gimbal Pro' }
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    const requiredFields = ['firstName', 'lastName', 'phone', 'email', 'address', 'city', 'state', 'postalCode', 'country', 'product', 'productId'];
    const errors = {};

    requiredFields.forEach(field => {
      if (!formData[field]) {
        errors[field] = 'This field is required';
      }
    });

    if (!formData.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the terms';
    }

    if (!formData.personalData) {
      errors.personalData = 'You must agree to allow us to store your personal data';
    }

    return errors;
  };

  const onFormSubmit = (e) => {
    e.preventDefault();
    setIsFormSubmitted(true);
    
    const errors = validateForm();
    
    if (Object.keys(errors).length === 0) {
      // Form is valid, submit the data
      console.log('Form submitted:', formData);
      setSuccessMsg('Registration submitted successfully!');
      setErrorMsg(null);
      
      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          phone: '',
          email: '',
          address: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          product: '',
          productId: '',
          agreeToTerms: false,
          marketingConsent: false,
          personalData: false,
          recaptcha: ''
        });
        setIsFormSubmitted(false);
        setSuccessMsg(null);
      }, 3000);
    } else {
      setErrorMsg('Please fill in all required fields');
      setSuccessMsg(null);
    }
  };

  const getFieldError = (fieldName) => {
    if (!isFormSubmitted) return false;
    if (fieldName === 'agreeToTerms' || fieldName === 'personalData') {
      return !formData[fieldName];
    }
    return !formData[fieldName];
  };

  return (
    <section className={styles.registrationSection}>
      <div className={styles.container}>
        <h1 className={styles.productTitle}>Product Registration Page</h1>
        <h2 className={styles.productSubtitle}>Product Warranty Registration</h2>
        <p className={styles.topParagraphText}>
        Once you receive the products from axldrone or our dealers, please follow the following process to register your products to activate warranty protection. 
        We will not be able to support or cover warranty claims for unregistered products.
        </p>

        <div className={styles.formContainer}>
          <h4 className={styles.enquiryTitle}>Make an Enquiry</h4>

          <form onSubmit={onFormSubmit}>
            <div className={styles.row}>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  First Name <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('firstName') ? styles.hasError : ''}`}
                  required
                />
              </div>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Last Name <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('lastName') ? styles.hasError : ''}`}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Phone Number <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('phone') ? styles.hasError : ''}`}
                  required
                />
              </div>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Email <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('email') ? styles.hasError : ''}`}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Street Address <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('address') ? styles.hasError : ''}`}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  City <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('city') ? styles.hasError : ''}`}
                  required
                />
              </div>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  State/Region <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('state') ? styles.hasError : ''}`}
                  required
                />
              </div>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Postal Code <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('postalCode') ? styles.hasError : ''}`}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.colMd4}>
                <label className={styles.labelText}>
                  Country/Region <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('country') ? styles.hasError : ''}`}
                  required
                />
              </div>
            </div>

            <div className={styles.row}>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Product <span className={styles.requiredStar}>*</span>
                </label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('product') ? styles.hasError : ''}`}
                  required
                >
                  <option value="">Select</option>
                  {products.map((product) => (
                    <option key={product.slug} value={product.slug}>
                      {product.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.col}>
                <label className={styles.labelText}>
                  Product ID <span className={styles.requiredStar}>*</span>
                </label>
                <input
                  type="text"
                  name="productId"
                  value={formData.productId}
                  onChange={handleInputChange}
                  className={`${styles.inputBottomLine} ${getFieldError('productId') ? styles.hasError : ''}`}
                  required
                />
              </div>
            </div>

            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                className={`${styles.checkboxInput} ${getFieldError('agreeToTerms') ? styles.hasCheckboxError : ''}`}
                required
              />
              <label className={styles.inputLabelText}>
                I agree to the axldrone warranty{' '}
                <a href="/terms-of-use" target="_parent" className={styles.termsLink}>
                  terms
                </a>
                <span className={styles.requiredStar}>*</span>
              </label>
            </div>

            <p className={styles.paragraphText}>
            axldrone India Pvt Ltd is committed to protecting and respecting your privacy, and we’ll only use your personal information to administer your account 
            and to provide the products and services you requested from us. From time to time, we would like to contact you about our products and services, as well 
            as other content that may be of interest to you. If you consent to us contacting you for this purpose, please tick below to say how you would like us to contact you:
            </p>

            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleInputChange}
                className={styles.checkboxInput}
              />
              <label className={styles.paragraphText}>
                I agree to receive Marketing communications like Phone Calls, SMS communications and Emails from axldrone
                Pvt Ltd.
              </label>
            </div>

            <p className={styles.paragraphText}>
            In order to provide you the content requested, we need to store and process your personal data. If you consent to us storing 
            your personal data for this purpose, please tick the checkbox below.
            </p>

            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                name="personalData"
                checked={formData.personalData}
                onChange={handleInputChange}
                className={`${styles.checkboxInput} ${getFieldError('personalData') ? styles.hasCheckboxError : ''}`}
                required
              />
              <label className={styles.paragraphText}>
                I agree to allow axldrone India Pvt Ltd to store and process my personal data.
                <span className={styles.requiredStar}>*</span>
              </label>
            </div>

            <div className={styles.textRight}>
              {successMsg && (
                <span className={`${styles.alert} ${styles.successGreen}`}>
                  {successMsg}
                </span>
              )}
              {errorMsg && (
                <span className={`${styles.alert} ${styles.errorRed}`}>
                  {errorMsg}
                </span>
              )}
              {!successMsg && !errorMsg && (
                <button type="submit" className={styles.btnPrimary}>
                  Submit
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ProductRegistration;